import PropTypes from 'prop-types'
import { Card, CardContent, CardHeader, Skeleton } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Chart } from 'src/components/chart'
import { legendIsolate, legendClickIsolate } from 'src/utils/chart-legend-isolate'

/**
 * 注文数とダウンロード数の日次推移。
 * 注文（棒）は決済確定日、ダウンロード（折れ線）はダウンロード日が軸で、
 * 過去に買った型紙の再ダウンロードもその日に計上されるため両者は一致しない。
 */
const useChartOptions = (categories) => {
  const theme = useTheme()

  return {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
      // 凡例クリックでその系列だけ表示（Grafana 風）
      events: { legendClick: legendClickIsolate },
    },
    colors: [theme.palette.primary.main, theme.palette.success.main],
    dataLabels: { enabled: false },
    fill: { opacity: [1, 0.9], type: 'solid' },
    grid: {
      borderColor: theme.palette.divider,
      strokeDashArray: 2,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    legend: { show: true, position: 'top', horizontalAlign: 'right', ...legendIsolate },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
    stroke: { curve: 'smooth', width: [0, 3] },
    theme: { mode: theme.palette.mode },
    tooltip: { shared: true, intersect: false },
    xaxis: {
      axisBorder: { color: theme.palette.divider, show: true },
      axisTicks: { color: theme.palette.divider, show: true },
      categories,
      // 期間が長いとラベルが潰れるため、Apex に間引かせる
      tickAmount: Math.min(categories.length, 12),
      labels: {
        style: { colors: theme.palette.text.secondary },
        // 'YYYY-MM-DD' は横幅を食うので M/D で表示する
        formatter: (v) => (typeof v === 'string' ? v.slice(5).replace('-', '/') : v),
      },
    },
    yaxis: {
      forceNiceScale: true,
      min: 0,
      labels: {
        style: { colors: theme.palette.text.secondary },
        formatter: (v) => `${Math.round(v)}`,
      },
    },
    states: { hover: { filter: { type: 'lighten', value: 0.1 } } },
    markers: { size: 0, hover: { size: 5 } },
    noData: { text: 'データがありません', style: { color: alpha(theme.palette.text.secondary, 0.8) } },
  }
}

export const PatternTrendChart = (props) => {
  const { loading = false, timeseries = [] } = props

  const categories = timeseries.map((p) => p.date)
  const options = useChartOptions(categories)
  const series = [
    { name: '注文数', type: 'column', data: timeseries.map((p) => p.orders) },
    { name: 'ダウンロード数', type: 'line', data: timeseries.map((p) => p.downloads) },
  ]

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader title="日次推移" subheader="注文は決済確定日、ダウンロードはダウンロード日で集計" />
      <CardContent>
        {loading ? (
          <Skeleton variant="rectangular" height={350} />
        ) : (
          <Chart height={350} options={options} series={series} type="line" width="100%" />
        )}
      </CardContent>
    </Card>
  )
}

PatternTrendChart.propTypes = {
  loading: PropTypes.bool,
  timeseries: PropTypes.array,
}
